import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

declare global {
  interface Window {
    Razorpay: new (options: RazorpayOptions) => RazorpayInstance;
  }
}

interface RazorpayOptions {
  key: string;
  amount: number;
  currency: string;
  name: string;
  description: string;
  order_id: string;
  prefill?: {
    email?: string;
    contact?: string;
    name?: string;
  };
  theme?: {
    color?: string;
  };
  handler: (response: RazorpayResponse) => void;
  modal?: {
    ondismiss?: () => void;
  };
}

interface RazorpayInstance {
  open: () => void;
  close: () => void;
}

interface RazorpayResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

type Plan = 'launch' | 'growth' | 'opspro';
type BillingCycle = 'monthly' | 'annual';

export function useRazorpay() {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const loadRazorpayScript = useCallback((): Promise<boolean> => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }, []);

  const initiatePayment = useCallback(
    async (plan: Plan, billingCycle: BillingCycle) => {
      setIsLoading(true);

      try {
        // Load Razorpay SDK
        const loaded = await loadRazorpayScript();
        if (!loaded) {
          throw new Error("Failed to load payment gateway");
        }

        // Create order via edge function
        const { data, error } = await supabase.functions.invoke(
          "razorpay-create-order",
          {
            body: { plan, billingCycle },
          }
        );

        if (error) throw error;

        const { orderId, amount, currency, keyId, prefill, notes } = data;

        // Open Razorpay checkout
        return new Promise<boolean>((resolve) => {
          const options: RazorpayOptions = {
            key: keyId,
            amount,
            currency,
            name: "Ritual",
            description: notes.planName,
            order_id: orderId,
            prefill,
            theme: {
              color: "#5B4FE5",
            },
            handler: async (response: RazorpayResponse) => {
              try {
                // Verify payment
                const { data: verifyData, error: verifyError } =
                  await supabase.functions.invoke("razorpay-verify-payment", {
                    body: {
                      razorpay_order_id: response.razorpay_order_id,
                      razorpay_payment_id: response.razorpay_payment_id,
                      razorpay_signature: response.razorpay_signature,
                      plan,
                      billingCycle,
                    },
                  });

                if (verifyError) throw verifyError;

                toast({
                  title: "Payment successful!",
                  description: `Your ${plan} plan is now active.`,
                });

                resolve(true);
              } catch (err) {
                console.error("Payment verification failed:", err);
                toast({
                  title: "Payment verification failed",
                  description: "Please contact support if amount was deducted.",
                  variant: "destructive",
                });
                resolve(false);
              } finally {
                setIsLoading(false);
              }
            },
            modal: {
              ondismiss: () => {
                setIsLoading(false);
                resolve(false);
              },
            },
          };

          const razorpay = new window.Razorpay(options);
          razorpay.open();
        });
      } catch (err) {
        console.error("Payment initiation failed:", err);
        toast({
          title: "Payment failed",
          description: "Unable to initiate payment. Please try again.",
          variant: "destructive",
        });
        setIsLoading(false);
        return false;
      }
    },
    [loadRazorpayScript, toast]
  );

  return {
    initiatePayment,
    isLoading,
  };
}
